// PriceRangeSection.jsx — Shop by budget with MUI icons + correct price-range links

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// MUI Icons
import LocalOfferOutlinedIcon    from "@mui/icons-material/LocalOfferOutlined";
import RedeemOutlinedIcon        from "@mui/icons-material/RedeemOutlined";
import AutoAwesomeOutlinedIcon   from "@mui/icons-material/AutoAwesomeOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import AttachMoneyIcon           from "@mui/icons-material/AttachMoney";
import ArrowForwardIcon          from "@mui/icons-material/ArrowForward";
import SentimentVerySatisfiedOutlinedIcon from "@mui/icons-material/SentimentVerySatisfiedOutlined";
import StarOutlinedIcon          from "@mui/icons-material/StarOutlined";
import RocketLaunchOutlinedIcon  from "@mui/icons-material/RocketLaunchOutlined";

const PRICE_RANGES = [
  {
    label: "Under $25",
    subtext: "Great Value Finds",
    Icon: LocalOfferOutlinedIcon,
    min: 0, max: 25,
    gradient: "from-pink-400 to-rose-500",
    lightBg: "from-pink-50 to-rose-50",
    border: "border-pink-100",
    tag: "Most Popular",
    tagGradient: "from-rose-500 to-pink-600",
  },
  {
    label: "$25 – $50",
    subtext: "Best Sellers",
    Icon: RedeemOutlinedIcon,
    min: 25, max: 50,
    gradient: "from-violet-500 to-purple-600",
    lightBg: "from-violet-50 to-purple-50",
    border: "border-violet-100",
    tag: "Best Value",
    tagGradient: "from-violet-500 to-purple-600",
  },
  {
    label: "$50 – $100",
    subtext: "Premium Selection",
    Icon: AutoAwesomeOutlinedIcon,
    min: 50, max: 100,
    gradient: "from-amber-400 to-orange-500",
    lightBg: "from-amber-50 to-orange-50",
    border: "border-amber-100",
    tag: "Trending",
    tagGradient: "from-amber-400 to-orange-500",
  },
  {
    label: "$100+",
    subtext: "Luxury Collection",
    Icon: WorkspacePremiumOutlinedIcon,
    min: 100, max: 9999,
    gradient: "from-emerald-500 to-teal-600",
    lightBg: "from-emerald-50 to-teal-50",
    border: "border-emerald-100",
    tag: "Luxury",
    tagGradient: "from-emerald-500 to-teal-600",
  },
];

const STATS = [
  { value: "23+", label: "Products",          Icon: RedeemOutlinedIcon,           iconColor: "#f0155a" },
  { value: "50K+",label: "Happy Customers",  Icon: SentimentVerySatisfiedOutlinedIcon, iconColor: "#8b5cf6" },
  { value: "4.9", label: "Average Rating",   Icon: StarOutlinedIcon,             iconColor: "#f59e0b" },
  { value: "24h", label: "Fast Delivery",    Icon: RocketLaunchOutlinedIcon,     iconColor: "#3b82f6" },
];

const containerVariants = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const cardVariants = {
  hidden:  { opacity: 0, y: 30, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] } },
};

export default function PriceRangeSection() {
  return (
    <section className="py-16 px-4" style={{ background: "linear-gradient(180deg, #fff8f0 0%, #ffffff 100%)" }}>
      <div className="max-w-7xl mx-auto">

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-amber-600 mb-3 bg-amber-50 px-4 py-1.5 rounded-full">
            <AttachMoneyIcon sx={{ fontSize: 14 }} /> Shop by Budget
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Find Your{" "}
            <span className="text-gradient-gold">Perfect Price</span>
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-lg mx-auto">
            Quality products at every price point. No compromises on value.
          </p>
        </motion.div>

        {/* Price range cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {PRICE_RANGES.map((range) => (
            <motion.div key={range.label} variants={cardVariants}>
              <Link
                to={`/?minPrice=${range.min}&maxPrice=${range.max}`}
                className={`group relative block rounded-3xl overflow-hidden border ${range.border} card-lift bg-white`}
              >
                {/* Tag badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r ${range.tagGradient} text-white px-2.5 py-1 rounded-full shadow-sm`}>
                    {range.tag}
                  </span>
                </div>

                {/* Gradient top area */}
                <div className={`bg-gradient-to-br ${range.lightBg} px-6 pt-8 pb-12 relative overflow-hidden`}>
                  {/* Decorative circle */}
                  <div className={`absolute -bottom-8 -right-8 w-28 h-28 rounded-full bg-gradient-to-br ${range.gradient} opacity-15 group-hover:opacity-25 transition-opacity`} />

                  <div className="group-hover:scale-110 transition-transform duration-300 origin-left mb-3 inline-block">
                    <range.Icon
                      sx={{
                        fontSize: 44,
                        background: `linear-gradient(135deg, var(--tw-gradient-stops))`,
                        color: range.gradient.includes("pink") ? "#f0155a"
                             : range.gradient.includes("violet") ? "#7c3aed"
                             : range.gradient.includes("amber") ? "#d97706"
                             : "#059669",
                      }}
                    />
                  </div>
                  <p className={`font-display text-2xl font-bold bg-gradient-to-r ${range.gradient} bg-clip-text text-transparent`}>
                    {range.label}
                  </p>
                </div>

                {/* Bottom content */}
                <div className="px-6 py-5">
                  <p className="text-sm text-gray-600 mb-4 font-medium">{range.subtext}</p>
                  <div className="flex items-center gap-1.5 text-sm font-bold text-rose-500 group-hover:gap-2.5 transition-all">
                    Shop Now <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6"
        >
          {STATS.map(({ value, label, Icon, iconColor }) => (
            <div key={label} className="bg-white rounded-2xl px-6 py-5 text-center shadow-card border border-gray-50">
              <div className="flex justify-center mb-2">
                <Icon sx={{ fontSize: 32, color: iconColor }} />
              </div>
              <div className="font-display text-3xl font-bold text-gray-900 mb-1">{value}</div>
              <div className="text-xs text-gray-500 font-medium">{label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
